///
/// EarthBrowser Admin Dashboard - System Management Section
///
/// Created: 2025-12-13
/// Copyright (c) 2025 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { toast } from "@/components/ui/use-toast"
import { useClearOGCCacheMutation } from "@/redux/api/systemApi"
import { Database, Info, Loader2, RefreshCw } from "lucide-react"
import { useState } from "react"

export function SystemManagementSection() {
  const [clearOGCCache, { isLoading: isClearing }] = useClearOGCCacheMutation()
  const [lastClearedAt, setLastClearedAt] = useState<Date | null>(null)

  const handleClearOGCCache = async () => {
    try {
      const result = await clearOGCCache().unwrap()

      setLastClearedAt(new Date())

      toast({
        title: "Cache Cleared",
        description: result.message || "OGC cache has been cleared successfully",
        variant: "default",
      })
    } catch (error: unknown) {
      const errorMessage = error && typeof error === "object" && "data" in error &&
        error.data && typeof error.data === "object" && "error" in error.data
        ? String(error.data.error)
        : "Failed to clear OGC cache"
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* OGC Cache Management */}
      <Card className="rounded-xl border border-border bg-background/50 backdrop-blur-sm transition-all duration-300">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Database className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            OGC Cache Management
          </CardTitle>
          <CardDescription>
            Clear cached OGC API - Features data to force refresh from the feature server
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted border border-border">
            <div className="flex-1">
              <h4 className="font-medium mb-1 text-sm">Collections Cache</h4>
              <p className="text-sm text-muted-foreground">
                Clears cached collection listings from the OGC feature server.
                This will force the next request to fetch fresh data from Railway.
              </p>
              {lastClearedAt && (
                <p className="text-xs text-muted-foreground mt-2">
                  Last cleared: {lastClearedAt.toLocaleString()}
                </p>
              )}
            </div>
            <Button
              onClick={handleClearOGCCache}
              disabled={isClearing}
              size="sm"
              variant="outline"
              className="shrink-0 rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:hover:scale-100"
            >
              {isClearing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Clearing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Clear Cache
                </>
              )}
            </Button>
          </div>

          <div className="text-xs text-muted-foreground space-y-1 p-3 rounded-xl bg-blue-500/5 border border-border">
            <p className="font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" />
              When to clear cache:
            </p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li>After updating feature tables in PostGIS</li>
              <li>After schema changes to the features database</li>
              <li>When testing OGC API endpoint changes</li>
              <li>If cached data appears stale or incorrect</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Future System Operations */}
      <Card className="rounded-xl border-dashed border-border bg-background/50 backdrop-blur-sm transition-all duration-300">
        <CardHeader>
          <CardTitle className="text-lg text-muted-foreground">More System Tools</CardTitle>
          <CardDescription>
            Additional system management tools will be added here
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Future features: Database health checks, service status monitoring,
            backup management, and system configuration.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

